import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useB3Lang } from '@b3/lang';
import { ArrowBackIosNew, InfoOutlined, OpenInNew } from '@mui/icons-material';
import { Box, Card, CardContent, Chip, Divider, Grid, Typography } from '@mui/material';

import B3Spin from '@/components/spin/B3Spin';
import { getCompanyData } from '@/shared/service/b2b';
import {
  fetchDeliveries,
  getCustomerAccounts,
  getProductMaterial,
} from '@/shared/service/request/alumio';
import { useAppSelector } from '@/store';

import { Flex, FlexItem } from './styled';

function DeliveryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [deliveryData, setDeliveryData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const b3Lang = useB3Lang();
  const companyB2BId = useAppSelector(({ company }) => company.companyInfo.id);
  const fetchDeliveryDetails = useCallback(
    async (deliveryId: any) => {
      setIsLoading(true);
      try {
        const resCompany = await getCompanyData(companyB2BId);
        const accountId = resCompany?.data?.extraFields.find(
          (field: any) => field.fieldName === 'account_id',
        )?.fieldValue;
        if (!accountId) {
          throw new Error('Account ID not found');
        }
        const params = { deliveryId, deliveryDatails: true };
        const response = await fetchDeliveries(accountId, params);

        if (!response || !response.results || response.results.length === 0) {
          console.warn('No delivery data found.');
          setDeliveryData(null);
          return;
        }

        const data = response?.results[0];

        const enrichedItems = await Promise.all(
          data.Item.map(async (item: any) => {
            const productId = item.ProductID;
            const productData = await getProductMaterial(productId);
            let productName = '';

            if (Array.isArray(productData?.Description)) {
              productName =
                productData.Description[0]?.Description?.['#'] || 'Producto no identificado';
            } else if (productData?.Description?.Description) {
              productName = productData.Description.Description['#'] || 'Producto no identificado';
            } else {
              productName = 'Producto no identificado';
            }

            return {
              ...item,
              ProductName: productName,
            };
          }),
        );

        data.Item = enrichedItems;

        if (data?.FreightForwarderParty?.PartyID) {
          const freightForwarderParty = await getCustomerAccounts(
            data.FreightForwarderParty.PartyID,
          );
          const FreightForwarderName =
            freightForwarderParty?.results?.[0]?.OrganisationSecondLineName;

          if (FreightForwarderName) {
            data.FreightForwarderName = FreightForwarderName;
          }
        }

        if (!data) {
          throw new Error(`HTTP error! status: ${data.status}`);
        }
        setDeliveryData(data);
      } catch (error) {
        console.error('Failed to fetch delivery details:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [companyB2BId],
  );
  useEffect(() => {
    if (id) {
      fetchDeliveryDetails(id);
    }
  }, [id, fetchDeliveryDetails]);
  const parseMsDate = (dateString: string): string => {
    const match = /\/Date\((\d+)\)\//.exec(dateString);
    if (match) {
      const date = new Date(parseInt(match[1], 10));
      return date.toLocaleString();
    }
    return dateString;
  };
  const goToDeliveries = () => {
    navigate('/deliveries');
  };
  return (
    <B3Spin isSpinning={isLoading} background="rgba(255,255,255,0.2)">
      <Box sx={{ overflow: 'auto', flex: 1, padding: '16px' }}>
        {/* Back Button */}
        <Box sx={{ marginBottom: '16px', width: 'fit-content' }}>
          <Box
            sx={{
              color: 'primary.main',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
            }}
            onClick={goToDeliveries}
          >
            {location.state !== null ? (
              <>
                <ArrowBackIosNew
                  sx={{
                    fontSize: '13px',
                    margin: '0 8px',
                  }}
                />
                <span>{b3Lang('deliveryDetail.backToDeliveries')}</span>
              </>
            ) : (
              ''
            )}
          </Box>
        </Box>
        {deliveryData ? (
          <Grid container spacing={3}>
            {/* Delivery Overview */}
            <Grid item xs={12}>
              <Card sx={{ boxShadow: 3, borderRadius: '1px' }}>
                <CardContent>
                  <Typography variant="h5" sx={{ fontWeight: 600 }} gutterBottom>
                    {b3Lang('deliveryDetail.deliveryOverview')}
                  </Typography>
                  <Divider sx={{ marginBottom: '16px' }} />
                  <Flex>
                    <FlexItem width="50%">
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ marginRight: '4px' }}
                      >
                        {b3Lang('deliveryDetail.deliveryID')}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {deliveryData.ID}
                      </Typography>
                    </FlexItem>
                    <FlexItem width="50%">
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ marginRight: '4px' }}
                      >
                        {b3Lang('deliveryDetail.deliveryType')}
                      </Typography>
                      <Typography variant="body1">{deliveryData.DeliveryTypeCodeText}</Typography>
                    </FlexItem>
                  </Flex>
                  <Flex>
                    <FlexItem width="50%">
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ marginRight: '4px' }}
                      >
                        {b3Lang('deliveryDetail.processingStatus')}
                      </Typography>
                      <Chip
                        label={deliveryData.DeliveryProcessingStatusCodeText}
                        color={
                          deliveryData.DeliveryProcessingStatusCodeText === 'Finished'
                            ? 'success'
                            : 'warning'
                        }
                        size="small"
                      />
                    </FlexItem>
                    <FlexItem width="50%">
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ marginRight: '4px' }}
                      >
                        {b3Lang('deliveryDetail.releaseStatus')}
                      </Typography>
                      <Chip
                        label={deliveryData.ReleaseStatusCodeText}
                        color="primary"
                        size="small"
                      />
                    </FlexItem>
                  </Flex>
                  <Flex>
                    <FlexItem width="50%">
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ marginRight: '4px' }}
                      >
                        {b3Lang('deliveryDetail.deliveryDate')}
                      </Typography>
                      <Typography variant="body1">
                        {parseMsDate(deliveryData.CreationDateTime)}
                      </Typography>
                    </FlexItem>
                  </Flex>
                </CardContent>
              </Card>
            </Grid>
            {/* Additional Info */}
            {deliveryData.FreightForwarderName && (
              <Grid item xs={12}>
                <Card
                  sx={{
                    backgroundColor: '#0288D1',
                    color: '#fff',
                    borderRadius: '1px',
                    boxShadow: 3,
                    padding: '16px',
                  }}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <InfoOutlined sx={{ fontSize: '30px' }} />
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {b3Lang('deliveryDetail.externalTracking')}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        {b3Lang('deliveryDetail.freightForwarder')}:{' '}
                        <strong>{deliveryData.FreightForwarderName}</strong>
                      </Typography>
                      {deliveryData.WebURI && (
                        <Typography
                          variant="body2"
                          sx={{ opacity: 0.9, display: 'flex', alignItems: 'center', gap: 0.5 }}
                        >
                          {b3Lang('deliveryDetail.trackingID')}:&nbsp;
                          <a
                            href={deliveryData.WebURI}
                            target="_blank"
                            style={{
                              color: '#fff',
                              textDecoration: 'underline',
                              fontWeight: 500,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                            }}
                          >
                            {deliveryData.ID1} <OpenInNew sx={{ fontSize: '16px' }} />
                          </a>
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}
            {deliveryData.Item && deliveryData.Item.length > 0 && (
              <Grid item xs={12}>
                <Card sx={{ boxShadow: 4, borderRadius: '1px', padding: '24px' }}>
                  <CardContent>
                    <Typography variant="h5" sx={{ fontWeight: 700, marginBottom: '24px' }}>
                      {b3Lang('deliveryDetail.itemDetails')}
                    </Typography>
                    <Grid container spacing={3}>
                      {deliveryData.Item.map((item: any) => (
                        <Grid item xs={12} key={item.ID}>
                          <Card
                            sx={{
                              borderRadius: 1,
                              backgroundColor: '#f9f9f9',
                              border: '1px solid #ddd',
                              boxShadow: 3,
                              padding: '24px',
                            }}
                          >
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: 700, marginBottom: '16px', color: '#333' }}
                            >
                              {b3Lang('deliveryDetail.itemID')}: {item.ID}
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                                  <strong>{b3Lang('deliveryDetail.productID')}:</strong>{' '}
                                  {item.ProductID}
                                </Typography>
                                <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                                  <strong>{b3Lang('deliveryDetail.productName')}:</strong>{' '}
                                  {item.ProductName}
                                </Typography>
                                <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                                  <strong>{b3Lang('deliveryDetail.quantity')}:</strong>{' '}
                                  {parseFloat(item.ItemDeliveryQuantity?.Quantity).toString()}{' '}
                                  {item.ItemDeliveryQuantity?.UnitCodeText}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        ) : (
          !isLoading && <Typography>{b3Lang('deliveryDetail.noDeliveryDataFound')}</Typography>
        )}
      </Box>
    </B3Spin>
  );
}
export default DeliveryDetail;
